"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { BellRing } from "lucide-react";

import { VehicleEventToast } from "@/components/notifications/VehicleEventToast";

import { useAuth } from "@/contexts/AuthContext";
import { useVehicleEventContext } from "@/hooks/use-vehicle-event-notifications";
import { isSoundEnabled, isToastEnabled } from "@/lib/api/notification-preferences";
import { useNotificationPrefsQuery } from "@/lib/api/queries";
import { playNotificationBeep } from "@/lib/notifications/beep";
import {
  addVehicleEvents,
  markVehicleEventRead,
  setVehicleEventUser,
  type StoredVehicleEvent,
} from "@/lib/notifications/event-store";
import {
  describeVehicleEvent,
  isFuelEventType,
  parseVehicleEvent,
  vehicleEventSeverity,
} from "@/lib/notifications/vehicle-events";
import { subscribeDeviceUpdates, type DeviceUpdateEnvelope } from "@/lib/realtime/vehicle-socket";
import { trackingFollowHref } from "@/lib/notifications/links";
import { clearVehicleAlerts, recordVehicleUpdate } from "@/lib/notifications/vehicle-alert-store";

/** Events are batched briefly so a burst (a tab waking up, a fleet crossing one zone) toasts once. */
const EVENT_FLUSH_MS = 1000;

/**
 * Sonner's icon slot is a fixed 16px box that also nudges its svg sideways; the tinted chip needs
 * 24px and no offset. Scoped to these toasts so every other toast keeps its stock layout.
 */
const ICON_SLOT_CLASS = "size-6! [&_svg]:mx-0!";

/**
 * `VehicleEventToast` draws its own rounded card. The Toaster's global `toast` class would still paint
 * Sonner's square wrapper behind it — background, border and shadow — so a rounded accent border sat
 * on a square box. Cleared here, for these toasts only.
 */
const CUSTOM_TOAST_WRAPPER_CLASS = "bg-transparent! border-0! shadow-none! p-0!";

/** Above this many new events in one batch, a single summary toast replaces the individual ones. */
const MAX_INDIVIDUAL_TOASTS = 5;

type PendingEvent = Omit<StoredVehicleEvent, "read">;

/**
 * Where the tracked point happened. The device's own `ts` is preferred — it is also what makes the
 * dedupe key stable across a redelivery — then the envelope's, then arrival time.
 */
function eventTimestamp(envelope: DeviceUpdateEnvelope): { key: string; iso: string } {
  const deviceTs = envelope.payload.ts;
  if (deviceTs) {
    const ms = Date.parse(deviceTs);
    if (Number.isFinite(ms)) return { key: deviceTs, iso: new Date(ms).toISOString() };
  }
  if (typeof envelope.ts === "number" && Number.isFinite(envelope.ts)) {
    const iso = new Date(envelope.ts).toISOString();
    return { key: String(envelope.ts), iso };
  }
  const iso = new Date().toISOString();
  return { key: iso, iso };
}

/**
 * Turns the live `events` on each vehicle update into toasts, app-wide.
 *
 * Mounted once in `Providers` rather than in `DashboardLayout`, which every view renders for
 * itself and so remounts on each navigation. Renders nothing. Everything the handlers read —
 * preferences, lookup lists, translations — goes through refs so the socket subscription depends
 * on the token alone and survives a language switch or a list refresh.
 */
export function VehicleEventListener() {
  const { token, user } = useAuth();
  const router = useRouter();
  const prefsQuery = useNotificationPrefsQuery();
  const context = useVehicleEventContext();

  useEffect(() => {
    setVehicleEventUser(user?.id ?? null);
  }, [user?.id]);

  const contextRef = useRef(context);
  const toastPrefsRef = useRef(prefsQuery.data?.toast);
  const showFuel = user?.features.fuel ?? true;
  const showFuelRef = useRef(showFuel);
  const soundPrefsRef = useRef(prefsQuery.data?.sound);
  const routerRef = useRef(router);
  useEffect(() => {
    contextRef.current = context;
    toastPrefsRef.current = prefsQuery.data?.toast;
    soundPrefsRef.current = prefsQuery.data?.sound;
    showFuelRef.current = showFuel;
    routerRef.current = router;
  }, [context, prefsQuery.data, router, showFuel]);

  useEffect(() => {
    if (!token || typeof window === "undefined") return;

    let pending: PendingEvent[] = [];
    let flushTimer: number | null = null;

    const showToasts = (added: StoredVehicleEvent[]) => {
      const ctx = contextRef.current;

      if (added.length > MAX_INDIVIDUAL_TOASTS) {
        toast.info(`${added.length} ${ctx.t.vehicleEvents.newNotifications}`, {
          icon: (
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-primary/15 text-primary">
              <BellRing className="h-3.5 w-3.5" strokeWidth={2.25} />
            </span>
          ),
          classNames: { toast: "border-l-4 border-l-primary", icon: ICON_SLOT_CLASS },
          action: {
            label: ctx.t.vehicleEvents.view,
            onClick: () => routerRef.current.push("/notifications"),
          },
        });
        return;
      }

      for (const entry of added) {
        const parsed = parseVehicleEvent(entry.raw);
        if (!parsed) continue;
        const { title, detail, vehicleName } = describeVehicleEvent(entry.vehicleId, parsed, ctx, {
          speed: entry.speed,
        });
        toast.custom(
          (toastId) => (
            <VehicleEventToast
              kind={parsed.type}
              severity={vehicleEventSeverity(parsed.type)}
              label={title}
              detail={detail}
              vehicle={ctx.vehiclesById.get(entry.vehicleId)}
              fallbackVehicleName={vehicleName}
              driverRef={entry.driverRef}
              driverNamesById={ctx.driverNamesById}
              actionLabel={ctx.t.vehicleEvents.view}
              onAction={() => {
                toast.dismiss(toastId);
                markVehicleEventRead(entry.id);
                routerRef.current.push(trackingFollowHref(entry.vehicleId));
              }}
            />
          ),
          { id: entry.id, classNames: { toast: CUSTOM_TOAST_WRAPPER_CLASS } },
        );
      }
    };

    const flush = () => {
      flushTimer = null;
      const batch = pending;
      pending = [];
      // The store drops anything it already holds, so only genuinely new events are toasted.
      const added = addVehicleEvents(batch);
      if (added.length === 0) return;
      showToasts(added);
      // One beep per batch, however many of its events are unmuted.
      const sound = soundPrefsRef.current;
      if (added.some((entry) => isSoundEnabled(sound, parseVehicleEvent(entry.raw)?.type ?? ""))) {
        playNotificationBeep();
      }
    };

    const unsubscribe = subscribeDeviceUpdates(token, (envelope) => {
      const rawEvents = envelope.payload.events;
      const events = Array.isArray(rawEvents)
        ? rawEvents.filter((raw): raw is string => typeof raw === "string")
        : [];
      const parsedEvents = events.flatMap((raw) => {
        const parsed = parseVehicleEvent(raw);
        return parsed ? [{ raw, parsed }] : [];
      });

      const { key, iso } = eventTimestamp(envelope);
      const { speed: rawSpeed } = envelope.payload;

      // Every update, every event, before the preference filter: a closing type the user muted must
      // still close its condition. The point's own speeding flags and speed also close a speeding
      // condition whose `*_end` never came; those ends come back to be toasted like real ones.
      const healedEnds = recordVehicleUpdate(
        envelope.deviceId,
        parsedEvents.map((entry) => entry.parsed),
        {
          isSpeeding: envelope.payload.is_speeding,
          isStreetSpeeding: envelope.payload.is_street_speeding,
          speed: typeof rawSpeed === "number" && Number.isFinite(rawSpeed) ? rawSpeed : undefined,
          at: Date.parse(iso),
        },
      );
      for (const parsed of healedEnds) {
        parsedEvents.push({ raw: parsed.arg ? `${parsed.type}:${parsed.arg}` : parsed.type, parsed });
      }

      if (parsedEvents.length === 0) return;

      const prefs = toastPrefsRef.current;
      const { speed, driver_id: driverId, lat, lon } = envelope.payload;
      // `0,0` is a device without a fix, as in LiveTracking's `invalidVehicleIds`.
      const hasPosition =
        typeof lat === "number" && Number.isFinite(lat) &&
        typeof lon === "number" && Number.isFinite(lon) &&
        !(lat === 0 && lon === 0);
      const point = {
        ...(hasPosition ? { lat, lon } : {}),
        speed: typeof speed === "number" && Number.isFinite(speed) ? speed : undefined,
        // `null` is "no driver"; only a missing field means "unknown, use the cached vehicle".
        driverRef: driverId === undefined ? undefined : (driverId ?? ""),
      };

      for (const { raw, parsed } of parsedEvents) {
        // A type switched off is neither toasted nor counted — only toasts feed the badge.
        if (!isToastEnabled(prefs, parsed.type)) continue;
        if (!showFuelRef.current && isFuelEventType(parsed.type)) continue;
        pending.push({
          id: `${envelope.deviceId}|${key}|${raw}`,
          vehicleId: envelope.deviceId,
          raw,
          occurredAt: iso,
          ...point,
        });
      }

      if (pending.length > 0 && flushTimer === null) {
        flushTimer = window.setTimeout(flush, EVENT_FLUSH_MS);
      }
    });

    return () => {
      unsubscribe();
      if (flushTimer !== null) window.clearTimeout(flushTimer);
      // Logout or a new login: one account's live state must not show under another.
      clearVehicleAlerts();
    };
  }, [token]);

  return null;
}
